import {
  Component,
  AfterViewInit,
  ElementRef,
  Input,
  NgZone,
  Output,
  EventEmitter,
  OnDestroy,
  Inject,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';

export const TURNSTILE_FIELD_NAME = 'turnstile.captchaResponse';

export interface TurnstileOptions {
  sitekey: string;
  action?: string;
  cData?: string;
  callback?: (token: string) => void;
  'error-callback'?: (errorCode: string) => boolean;
  'expired-callback'?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  language?: string;
  tabindex?: number;
  appearance?: 'always' | 'execute' | 'interaction-only';
}

declare global {
  interface Window {
    onloadTurnstileCallback: () => void;
    turnstile: {
      render: (
        idOrContainer: string | HTMLElement,
        options: TurnstileOptions,
      ) => string;
      reset: (widgetIdOrContainer: string | HTMLElement) => void;
      getResponse: (
        widgetIdOrContainer: string | HTMLElement,
      ) => string | undefined;
      remove: (widgetIdOrContainer: string | HTMLElement) => void;
    };
  }
}

const SCRIPT_ID = 'ngx-turnstile';
const CALLBACK_NAME = 'onloadTurnstileCallback';
type SupportedVersion = '0';

@Component({
  selector: 'ngx-turnstile',
  template: ``,
  exportAs: 'ngx-turnstile',
})
export class NgxTurnstileComponent implements AfterViewInit, OnDestroy {
  @Input() siteKey!: string;
  @Input() action?: string;
  @Input() cData?: string;
  @Input() theme?: 'light' | 'dark' | 'auto' = 'auto';
  @Input() language?: string = 'auto';
  @Input() version: SupportedVersion = '0';
  @Input() tabIndex?: number;
  @Input() appearance?: 'always' | 'execute' | 'interaction-only' = 'always';

  @Output() resolved = new EventEmitter<string | null>();
  @Output() errored = new EventEmitter<string | null>();

  private widgetId!: string;

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private zone: NgZone,
    @Inject(DOCUMENT) private document: Document,
  ) {
    console.log('[NgxTurnstile] Component constructed', { elementRef: !!elementRef, zone: !!zone });
  }

  ngAfterViewInit(): void {
    console.log('[NgxTurnstile] ngAfterViewInit called');
    this.createWidget();
  }

  private _getCloudflareTurnstileUrl(): string {
    if (this.version === '0') {
      return 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    }
    throw 'Version not defined in ngx-turnstile component.';
  }

  public createWidget(): void {
    console.log('[NgxTurnstile] Creating Turnstile widget...');

    const turnstileOptions: TurnstileOptions = {
      sitekey: this.siteKey,
      theme: this.theme,
      language: this.language,
      tabindex: this.tabIndex,
      action: this.action,
      cData: this.cData,
      appearance: this.appearance,
      callback: (token: string) => {
        console.log('[NgxTurnstile] Callback triggered');
        try {
          this.zone.run(() => {
            console.log('[NgxTurnstile] Emitting resolved token inside zone');
            this.resolved.emit(token);
          });
        } catch (err) {
          console.error('[NgxTurnstile] ERROR inside callback:', err);
        }
      },
      'error-callback': (errorCode: string): boolean => {
        console.warn('[NgxTurnstile] Error callback triggered', { errorCode, thisCtx: this });
        this.zone.run(() => this.errored.emit(errorCode));
        return false;
      },
      'expired-callback': () => {
        console.log('[NgxTurnstile] Expired callback triggered');
        this.zone.run(() => this.reset());
      },
    };

    window[CALLBACK_NAME] = () => {
      console.log('[NgxTurnstile] onloadTurnstileCallback executed', { element: this.elementRef.nativeElement });
      if (!this.elementRef?.nativeElement) {
        console.warn('[NgxTurnstile] No elementRef.nativeElement found');
        return;
      }

      try {
        this.widgetId = window.turnstile.render(
          this.elementRef.nativeElement,
          turnstileOptions,
        );
        console.log('[NgxTurnstile] Turnstile widget rendered', { widgetId: this.widgetId });
      } catch (err) {
        console.error('[NgxTurnstile] ERROR rendering Turnstile widget:', err);
      }
    };

    if (this.scriptLoaded()) {
      console.log('[NgxTurnstile] Script already loaded — invoking callback');
      window[CALLBACK_NAME]();
      return;
    }

    console.log('[NgxTurnstile] Loading Cloudflare Turnstile script...');
    const script = this.document.createElement('script');
    script.src = `${this._getCloudflareTurnstileUrl()}?render=explicit&onload=${CALLBACK_NAME}`;
    script.id = SCRIPT_ID;
    script.async = true;
    script.defer = true;
    this.document.head.appendChild(script);
  }

  public reset(): void {
    console.log('[NgxTurnstile] Reset called');
    if (this.widgetId) {
      this.resolved.emit(null);
      window.turnstile.reset(this.widgetId);
      console.log('[NgxTurnstile] Widget reset successfully');
    } else {
      console.warn('[NgxTurnstile] Tried to reset before widget initialized');
    }
  }

  public ngOnDestroy(): void {
    console.log('[NgxTurnstile] ngOnDestroy called');
    if (this.widgetId) {
      window.turnstile.remove(this.widgetId);
      console.log('[NgxTurnstile] Widget removed on destroy');
    }
  }

  public scriptLoaded(): boolean {
    const loaded = !!this.document.getElementById(SCRIPT_ID);
    console.log('[NgxTurnstile] scriptLoaded:', loaded);
    return loaded;
  }
}
